/**
 * NT Token Package Icon Mapping Utility
 * Maps package names to their corresponding tier-specific icons
 * Provides graceful fallback for unknown package types
 */

/**
 * Maps NT token package names to their corresponding tier icon paths
 *
 * @param packageName - The name of the package (e.g., "NT Starter Pack", "NT Value Pack", "NT Premium Pack")
 * @returns Path to the appropriate tier icon, or fallback to generic football icon
 *
 * @example
 * getPackageIconPath("NT Starter Pack") // Returns "/icons/nav/NcoinBronzepakage.png"
 * getPackageIconPath("NT Value Pack")   // Returns "/icons/nav/NcoinSilverpakage.png"
 * getPackageIconPath("NT Premium Pack") // Returns "/icons/nav/NcoinGoldpakage.png"
 * getPackageIconPath("Unknown Package") // Returns "/football_icon.svg" (fallback)
 */
export function getPackageIconPath(packageName: string): string {
  // Normalize package name to handle variations
  const normalizedName = packageName.toLowerCase().trim()

  // Map package names to tier icons
  if (normalizedName.includes('starter')) {
    return '/icons/nav/NcoinBronzepakage.png'
  }

  if (normalizedName.includes('value')) {
    return '/icons/nav/NcoinSilverpakage.png'
  }

  if (normalizedName.includes('premium')) {
    return '/icons/nav/NcoinGoldpakage.png'
  }

  // Fallback to generic football icon for unknown packages
  return '/football_icon.svg'
}

/**
 * Gets accessible label for package tier
 *
 * @param packageName - The name of the package
 * @returns Descriptive label for screen readers and alt text
 *
 * @example
 * getPackageTierLabel("NT Starter Pack")  // Returns "Bronze Starter Package"
 * getPackageTierLabel("NT Value Pack")    // Returns "Silver Value Package"
 * getPackageTierLabel("NT Premium Pack")  // Returns "Gold Premium Package"
 */
export function getPackageTierLabel(packageName: string): string {
  const normalizedName = packageName.toLowerCase().trim()

  if (normalizedName.includes('starter')) {
    return 'Bronze Starter Package'
  }

  if (normalizedName.includes('value')) {
    return 'Silver Value Package'
  }

  if (normalizedName.includes('premium')) {
    return 'Gold Premium Package'
  }

  // Fallback to generic label
  return 'NT Token Package'
}
